import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useDevMode } from '../contexts/DevModeContext';
import { Clock, Calendar, Edit2, Save, X, Trash2, Search } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyHours = () => {
  const { user, token } = useAuth();
  const { devMode } = useDevMode();
  const [loading, setLoading] = useState(true);
  const [allHours, setAllHours] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingHours, setEditingHours] = useState(null);
  const [editHoursValue, setEditHoursValue] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editTaskId, setEditTaskId] = useState('');
  const [editDate, setEditDate] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedHours, setSelectedHours] = useState(null);
  const [projects, setProjects] = useState([]);
  const [editGEId, setEditGEId] = useState('');

  useEffect(() => {
    if (user && token) {
      fetchHours();
      fetchTasks();
      fetchProjects();
    }
  }, [user, token]);

  const fetchHours = async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      console.log('Fetching hours for user:', user.id);
      const response = await axios.get(`${API}/hours/user/${user.id}`, {
        headers,
        params: { rows_per_page: 1000 }
      });

      console.log('Hours API Response:', response.data);
      console.log('Hours fetched:', response.data.records?.length);
      console.log('Sample hour record:', response.data.records?.[0]);
      setAllHours(response.data.records || []);
    } catch (error) {
      console.error('Failed to fetch hours:', error);
      console.error('Error details:', error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const fetchTasks = async () => {
    if (!user?.id) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/tasks/user/${user.id}`, {
        headers,
        params: { rows_per_page: 1000 }
      });

      setTasks(response.data.records || []);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    }
  };

  const fetchProjects = async () => {
    if (!user?.id) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/projects/user/${user.id}`, {
        headers,
        params: { rows_per_page: 1000 }
      });

      setProjects(response.data.records || []);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  };

  const categorizeHours = () => {
    const now = new Date();
    const today = now.toDateString();
    
    console.log('=== CATEGORIZING HOURS ===');
    console.log('Total hours records:', allHours.length);
    
    // Calculate date ranges for current week and last 3 weeks
    // Start of current week (Sunday)
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    startOfWeek.setHours(0, 0, 0, 0);
    
    // End of current week (Saturday 23:59:59)
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);
    
    const startOfWeek1 = new Date(startOfWeek);
    startOfWeek1.setDate(startOfWeek.getDate() - 7);
    
    const endOfWeek1 = new Date(startOfWeek1);
    endOfWeek1.setDate(startOfWeek1.getDate() + 6);
    endOfWeek1.setHours(23, 59, 59, 999);
    
    const startOfWeek2 = new Date(startOfWeek);
    startOfWeek2.setDate(startOfWeek.getDate() - 14);
    
    const endOfWeek2 = new Date(startOfWeek2);
    endOfWeek2.setDate(startOfWeek2.getDate() + 6);
    endOfWeek2.setHours(23, 59, 59, 999);
    
    const startOfWeek3 = new Date(startOfWeek);
    startOfWeek3.setDate(startOfWeek.getDate() - 21);
    
    const endOfWeek3 = new Date(startOfWeek3);
    endOfWeek3.setDate(startOfWeek3.getDate() + 6);
    endOfWeek3.setHours(23, 59, 59, 999);
    
    console.log('Current week:', startOfWeek, 'to', endOfWeek);
    console.log('Last week:', startOfWeek1, 'to', endOfWeek1);

    const categories = {
      currentWeek: [],
      lastWeek: [],
      twoWeeksAgo: [],
      threeWeeksAgo: [],
      older: []
    };

    let skippedNoDate = 0;
    let skippedInvalidDate = 0;
    let skippedNoTask = 0;
    let processed = 0;

    allHours.forEach(record => {
      const dateField = record.field_37_raw || record.field_37;
      if (!dateField) {
        skippedNoDate++;
        console.log('Skipped - no date:', record.id);
        return;
      }
      
      // Parse date - handle both string and Knack date object
      let dateStr;
      if (typeof dateField === 'object' && dateField.date) {
        // Knack date object: {date: '10/25/2025', date_formatted: '10/25/2025', ...}
        dateStr = dateField.date;
      } else if (typeof dateField === 'string') {
        dateStr = dateField;
      } else {
        skippedInvalidDate++;
        console.log('Skipped - invalid date format:', dateField, record.id);
        return;
      }
      
      // Parse date properly
      const recordDate = new Date(dateStr);
      if (isNaN(recordDate.getTime())) {
        skippedInvalidDate++;
        console.log('Skipped - invalid date:', dateStr, record.id);
        return;
      }
      recordDate.setHours(0, 0, 0, 0);
      
      // Check task connection - try both raw and processed fields
      const taskConnection = record.field_182_raw || record.field_182 || [];
      console.log('Record', record.id, '- field_182:', record.field_182, 'field_182_raw:', record.field_182_raw, 'Type:', typeof taskConnection, 'Length:', taskConnection?.length);
      
      // Skip records with no task connection (dummy/old records)
      if (!taskConnection || (Array.isArray(taskConnection) && taskConnection.length === 0)) {
        skippedNoTask++;
        console.log('Skipped - no task connection:', record.id);
        return;
      }
      
      processed++;
      
      // Get task name - handle both array of objects and array of strings
      let taskName = '';
      if (Array.isArray(taskConnection) && taskConnection.length > 0) {
        if (typeof taskConnection[0] === 'object' && taskConnection[0].identifier) {
          taskName = taskConnection[0].identifier;
        } else if (typeof taskConnection[0] === 'string') {
          // It's just an ID, try to find task name from tasks list
          const taskId = taskConnection[0];
          const task = tasks.find(t => t.id === taskId);
          taskName = task ? (task.field_43_raw || task.field_43 || 'Unknown Task') : taskId;
        }
      }
      
      const notes = record.field_38_raw || record.field_38 || '';
      
      // Filter by search term
      if (searchTerm && !taskName.toLowerCase().includes(searchTerm.toLowerCase()) && 
          !notes.toLowerCase().includes(searchTerm.toLowerCase())) {
        return;
      }

      if (recordDate >= startOfWeek && recordDate <= endOfWeek) {
        console.log('Current week:', recordDate);
        categories.currentWeek.push(record);
      } else if (recordDate >= startOfWeek1 && recordDate <= endOfWeek1) {
        console.log('Last week:', recordDate);
        categories.lastWeek.push(record);
      } else if (recordDate >= startOfWeek2 && recordDate <= endOfWeek2) {
        categories.twoWeeksAgo.push(record);
      } else if (recordDate >= startOfWeek3 && recordDate <= endOfWeek3) {
        categories.threeWeeksAgo.push(record);
      } else {
        categories.older.push(record);
      }
    });

    console.log('Categorization complete:');
    console.log('- Skipped (no date):', skippedNoDate);
    console.log('- Skipped (invalid date):', skippedInvalidDate);
    console.log('- Skipped (no task):', skippedNoTask);
    console.log('- Processed:', processed);
    console.log('- Current week:', categories.currentWeek.length);
    console.log('- Last week:', categories.lastWeek.length);
    console.log('- 2 weeks ago:', categories.twoWeeksAgo.length);
    console.log('- 3 weeks ago:', categories.threeWeeksAgo.length);
    console.log('- Older:', categories.older.length);

    return categories;
  };

  const formatDate = (dateField) => {
    if (!dateField) return 'N/A';
    
    // Handle Knack date object
    let dateStr;
    if (typeof dateField === 'object' && dateField.date) {
      dateStr = dateField.date;
    } else {
      dateStr = dateField;
    }
    
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return 'Invalid Date';
    
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatHours = (hours) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
  };

  const getTotalHours = (records) => {
    return records.reduce((sum, r) => sum + (parseFloat(r.field_36_raw || r.field_36) || 0), 0);
  };

  const startInlineEdit = (record) => {
    setEditingHours(record.id);
    setEditHoursValue(record.field_36_raw || record.field_36 || '');
    setEditNotes(record.field_38_raw || record.field_38 || '');
  };

  const cancelInlineEdit = () => {
    setEditingHours(null);
    setEditHoursValue('');
    setEditNotes('');
  };

  const saveInlineEdit = async (hoursId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(
        `${API}/hours/${hoursId}`,
        {
          field_36: parseFloat(editHoursValue),
          field_38: editNotes
        },
        { headers }
      );
      
      cancelInlineEdit();
      await fetchHours();
    } catch (error) {
      console.error('Error updating hours:', error);
      alert('Failed to update hours');
    }
  };

  const deleteHours = async (hoursId) => {
    if (!confirm('Delete this hours entry?')) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/hours/${hoursId}`, { headers });
      await fetchHours();
    } catch (error) {
      console.error('Error deleting hours:', error);
      alert('Failed to delete hours');
    }
  };

  const openEditModal = (record) => {
    setSelectedHours(record);
    setEditHoursValue(record.field_36_raw || record.field_36 || '');
    setEditNotes(record.field_38_raw || record.field_38 || '');
    
    // Get date - field_37 is YYYY-MM-DD format
    const dateValue = record.field_37 || '';
    setEditDate(dateValue);
    
    const taskConnection = record.field_182_raw || [];
    const taskId = taskConnection.length > 0 ? taskConnection[0].id : '';
    setEditTaskId(taskId);
    
    // Get GE from task connection
    const geConnection = record.field_190_raw || [];
    const geId = geConnection.length > 0 ? geConnection[0].id : '';
    setEditGEId(geId);
    
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setSelectedHours(null);
    setEditHoursValue('');
    setEditNotes('');
    setEditTaskId('');
    setEditDate('');
  };

  const saveEditModal = async () => {
    if (!selectedHours) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(
        `${API}/hours/${selectedHours.id}`,
        {
          field_182: editTaskId ? [editTaskId] : [],
          field_190: editGEId ? [editGEId] : [],
          field_36: parseFloat(editHoursValue),
          field_37: editDate, // Date in YYYY-MM-DD format
          field_38: editNotes
        },
        { headers }
      );
      
      closeEditModal();
      await fetchHours();
    } catch (error) {
      console.error('Error updating hours:', error);
      alert('Failed to update hours');
    }
  };

  const renderHoursRow = (record, index) => {
    const taskConnection = record.field_182_raw || record.field_182 || [];
    
    // Get task name - handle both array of objects and array of strings
    let taskName = 'No Task';
    if (Array.isArray(taskConnection) && taskConnection.length > 0) {
      if (typeof taskConnection[0] === 'object' && taskConnection[0].identifier) {
        taskName = taskConnection[0].identifier;
      } else if (typeof taskConnection[0] === 'string') {
        // It's just an ID, try to find task name from tasks list
        const taskId = taskConnection[0];
        const task = tasks.find(t => t.id === taskId);
        taskName = task ? (task.field_43_raw || task.field_43 || 'Unknown Task') : taskId;
      }
    }
    
    const hours = parseFloat(record.field_36_raw || record.field_36) || 0;
    
    // Try multiple field variations for notes
    const notes = record.field_38_raw || record.field_38 || record.field_114_raw || record.field_114 || '';
    
    const date = record.field_37_raw || record.field_37;
    
    const geConnection = record.field_190_raw || [];
    const geName = geConnection.length > 0 && geConnection[0].identifier ? geConnection[0].identifier : '';
    
    // Display as "GE - Task Name" if GE exists, otherwise just task name
    const displayTaskName = geName ? `${geName} - ${taskName}` : taskName;

    return (
      <tr key={record.id} className={`${index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700/50'} hover:bg-blue-50 dark:hover:bg-blue-900/20`}>
        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
          {formatDate(date)}
        </td>
        <td className="px-4 py-3">
          <span className="text-sm text-gray-900 dark:text-white">
            {displayTaskName}
          </span>
        </td>
        <td className="px-4 py-3">
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            {formatHours(hours)}
          </span>
        </td>
        <td className="px-4 py-3">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {notes || '-'}
          </span>
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <button
              onClick={() => openEditModal(record)}
              className="p-1 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded"
              title="Edit"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            <button
              onClick={() => deleteHours(record.id)}
              className="p-1 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  const renderCategory = (title, records, icon) => {
    if (records.length === 0) return null;

    const totalHours = getTotalHours(records);

    return (
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3 pb-2 border-b-2 border-blue-500 dark:border-blue-400">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            {icon}
            {title}
          </h3>
          <span className="text-lg font-bold text-blue-600 dark:text-blue-400">
            {formatHours(totalHours)} ({records.length} entries)
          </span>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-100 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">Task</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">Hours</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">Notes</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {records.map((record, index) => renderHoursRow(record, index))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const categories = categorizeHours();
  const currentWeekTotal = getTotalHours(categories.currentWeek);
  const lastWeekTotal = getTotalHours(categories.lastWeek);
  const twoWeeksAgoTotal = getTotalHours(categories.twoWeeksAgo);
  const threeWeeksAgoTotal = getTotalHours(categories.threeWeeksAgo);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Clock className="w-12 h-12 text-blue-600 dark:text-blue-400 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading hours...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
          <Clock className="w-7 h-7 text-blue-600 dark:text-blue-400" />
          My Hours
        </h1>
        <p className="text-gray-600 dark:text-gray-400">View and manage all your logged hours</p>
      </div>

      {/* Search and Stats */}
      <div className="mb-6">
        <div className="flex items-center gap-4 mb-4">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by task or notes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
        
        {/* Week Totals */}
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-green-50 dark:bg-green-900/20 px-4 py-3 rounded-lg border border-green-200 dark:border-green-800">
            <div className="text-xs text-green-600 dark:text-green-400 font-medium">Current Week</div>
            <div className="text-xl font-bold text-green-600 dark:text-green-400">
              {formatHours(currentWeekTotal)}
            </div>
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/20 px-4 py-3 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="text-xs text-blue-600 dark:text-blue-400 font-medium">Last Week</div>
            <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
              {formatHours(lastWeekTotal)}
            </div>
          </div>
          <div className="bg-purple-50 dark:bg-purple-900/20 px-4 py-3 rounded-lg border border-purple-200 dark:border-purple-800">
            <div className="text-xs text-purple-600 dark:text-purple-400 font-medium">2 Weeks Ago</div>
            <div className="text-xl font-bold text-purple-600 dark:text-purple-400">
              {formatHours(twoWeeksAgoTotal)}
            </div>
          </div>
          <div className="bg-orange-50 dark:bg-orange-900/20 px-4 py-3 rounded-lg border border-orange-200 dark:border-orange-800">
            <div className="text-xs text-orange-600 dark:text-orange-400 font-medium">3 Weeks Ago</div>
            <div className="text-xl font-bold text-orange-600 dark:text-orange-400">
              {formatHours(threeWeeksAgoTotal)}
            </div>
          </div>
        </div>
      </div>

      {/* Categories */}
      <div className="space-y-6">
        {renderCategory('Current Week', categories.currentWeek, <Calendar className="w-5 h-5 text-green-600" />)}
        {renderCategory('Last Week', categories.lastWeek, <Calendar className="w-5 h-5 text-blue-600" />)}
        {renderCategory('2 Weeks Ago', categories.twoWeeksAgo, <Calendar className="w-5 h-5 text-purple-600" />)}
        {renderCategory('3 Weeks Ago', categories.threeWeeksAgo, <Calendar className="w-5 h-5 text-orange-600" />)}
        {renderCategory('Older', categories.older, <Calendar className="w-5 h-5 text-gray-400" />)}
        
        {allHours.length === 0 && (
          <div className="text-center py-12">
            <Clock className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">No hours logged yet</p>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && selectedHours && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">Edit Hours Entry</h3>
                <button
                  onClick={closeEditModal}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Task
                </label>
                <select
                  value={editTaskId}
                  onChange={(e) => {
                    const selectedTaskId = e.target.value;
                    setEditTaskId(selectedTaskId);
                    
                    // Auto-populate GE from selected task
                    if (selectedTaskId) {
                      const selectedTask = tasks.find(t => t.id === selectedTaskId);
                      const taskGEId = selectedTask?.field_48_raw?.[0]?.id || '';
                      if (taskGEId && taskGEId !== editGEId) {
                        setEditGEId(taskGEId);
                      }
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a task...</option>
                  {tasks
                    .filter(task => {
                      // If GE is selected, only show tasks from that GE
                      if (!editGEId) return true;
                      const taskGEId = task.field_48_raw?.[0]?.id || '';
                      return taskGEId === editGEId;
                    })
                    .map(task => {
                      const taskName = task.field_43_raw || task.field_43 || 'Unnamed Task';
                      const projectGE = task.field_48_raw?.[0]?.field_2_raw || '';
                      // Format: "Task name (GE123)"
                      const displayName = projectGE ? `${taskName} (${projectGE})` : taskName;
                      return (
                        <option key={task.id} value={task.id}>
                          {displayName}
                        </option>
                      );
                    })}
                </select>
                {editGEId && (
                  <p className="mt-1 text-xs text-blue-600 dark:text-blue-400">
                    {tasks.filter(task => {
                      const taskGEId = task.field_48_raw?.[0]?.id || '';
                      return taskGEId === editGEId;
                    }).length} tasks from selected GE
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  GE (Project)
                </label>
                <select
                  value={editGEId}
                  onChange={(e) => {
                    const selectedGEId = e.target.value;
                    setEditGEId(selectedGEId);
                    
                    // Clear task if it doesn't belong to selected GE
                    if (selectedGEId && editTaskId) {
                      const currentTask = tasks.find(t => t.id === editTaskId);
                      const taskGEId = currentTask?.field_48_raw?.[0]?.id || '';
                      if (taskGEId !== selectedGEId) {
                        setEditTaskId(''); // Clear task selection
                      }
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a project (GE)...</option>
                  {projects.map(project => {
                    const geCode = project.field_2_raw || project.field_2 || 'No GE';
                    const projectName = project.field_5_raw || project.field_5 || 'Unnamed Project';
                    // Format: "GE123 - Project Name"
                    const displayName = `${geCode} - ${projectName}`;
                    return (
                      <option key={project.id} value={project.id}>
                        {displayName}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Date
                </label>
                <input
                  type="date"
                  value={editDate}
                  onChange={(e) => setEditDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Hours
                </label>
                <input
                  type="number"
                  step="0.25"
                  value={editHoursValue}
                  onChange={(e) => setEditHoursValue(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Notes
                </label>
                <textarea
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={closeEditModal}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={saveEditModal}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyHours;
